import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Divider, Tab, Tabs, Typography } from '@mui/material';
import RoutesTable from './RoutesTable';
import { RouteSubTab, RouteType } from './types';
import { RouteTabConfig } from './config/constant';
import Icons from '../../components/icons';
import { SnackbarSeverityType } from '../../enums';
import AddRouteDialog from './components/AddRouteDialog';
import EditRouteDialog from './components/EditRouteDialog';
import pallet from '../../layouts/pallet';
import DialogModal from '../../components/DialogModal';

import { getRoutes } from '../../apis/transport/routes';
import { getBuses } from '../../apis/transport/buses';
import { User } from '../../types/userType';
import { RouteWithDetailedView, Bus } from './types';
import { sendRouteAlert, deleteRoute } from '../../apis/transport/routes';

interface RoutesContainerProps {
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  loggedInUser: User;
}

const RoutesContainer: React.FC<RoutesContainerProps> = ({
  showToast,
  loggedInUser
}) => {
  const [activeSubTab, setActiveSubTab] = useState<string>(RouteSubTab.All);
  const [routes, setRoutes] = useState<RouteWithDetailedView[]>([]);
  const [buses, setBuses] = useState<Bus[]>([]);
  const [filteredRoutes, setFilteredRoutes] = useState<RouteWithDetailedView[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [isSOSConfirmDialogOpen, setIsSOSConfirmDialogOpen] = useState(false);
  const [routeForSOS, setRouteForSOS] = useState<RouteWithDetailedView | null>(
    null
  );
  const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] =
    useState(false);
  const [routeToDelete, setRouteToDelete] =
    useState<RouteWithDetailedView | null>(null);
  const [isAddRouteDialogOpen, setIsAddRouteDialogOpen] = useState(false);
  const [isEditRouteDialogOpen, setIsEditRouteDialogOpen] = useState(false);
  const [routeToEdit, setRouteToEdit] = useState<RouteWithDetailedView | null>(
    null
  );

  const handleSubTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    const tabId = RouteTabConfig[newValue]?.id;
    if (tabId) {
      setActiveSubTab(tabId);
    }
  };

  const fetchBuses = useCallback(async () => {
    try {
      const response = await getBuses(loggedInUser.school);
      setBuses(response.data as unknown as Bus[]);
    } catch (error) {
      showToast('Error fetching buses', SnackbarSeverityType.error);
    }
  }, [loggedInUser.school, showToast]);

  const fetchRoutes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getRoutes(loggedInUser.school);
      setRoutes(response as unknown as RouteWithDetailedView[]);
    } catch (error) {
      showToast('Error fetching routes', SnackbarSeverityType.error);
    } finally {
      setLoading(false);
    }
  }, [loggedInUser.school, showToast]);

  useEffect(() => {
    if (loggedInUser?.school) {
      fetchRoutes();
      fetchBuses();
    }
  }, [loggedInUser?.school, fetchRoutes, fetchBuses]);

  useEffect(() => {
    if (activeSubTab === RouteSubTab.All) {
      setFilteredRoutes(routes);
    } else if (activeSubTab === RouteSubTab.PICK_UP_ROUTES) {
      setFilteredRoutes(
        routes.filter((route) => route.type === RouteType.PICK_UP_ROUTE)
      );
    } else if (activeSubTab === RouteSubTab.DROP_OFF_ROUTES) {
      setFilteredRoutes(
        routes.filter((route) => route.type === RouteType.DROP_OFF_ROUTE)
      );
    }
  }, [routes, activeSubTab]);

  const handleAddRoute = () => {
    fetchRoutes();
  };

  const handleEditRoute = (route: RouteWithDetailedView) => {
    setRouteToEdit(route);
    setIsEditRouteDialogOpen(true);
  };

  const handleEditRouteSuccess = () => {
    setRouteToEdit(null);
    fetchRoutes();
  };

  const handleDeleteRoute = (route: RouteWithDetailedView) => {
    setRouteToDelete(route);
    setIsDeleteConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (routeToDelete) {
      try {
        await deleteRoute(routeToDelete.id);
        showToast('Route deleted successfully!', SnackbarSeverityType.success);
        fetchRoutes();
      } catch (error) {
        showToast('Error deleting route', SnackbarSeverityType.error);
      } finally {
        setIsDeleteConfirmDialogOpen(false);
        setRouteToDelete(null);
      }
    }
  };

  const handleSOSAlert = (route: RouteWithDetailedView) => {
    setRouteForSOS(route);
    setIsSOSConfirmDialogOpen(true);
  };

  const handleConfirmSOS = async () => {
    if (routeForSOS) {
      try {
        await sendRouteAlert(routeForSOS.id);
        showToast('SOS alert sent successfully!', SnackbarSeverityType.success);
      } catch (error) {
        showToast('Error sending SOS alert', SnackbarSeverityType.error);
      } finally {
        setIsSOSConfirmDialogOpen(false);
        setRouteForSOS(null);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Tabs
          value={RouteTabConfig.findIndex((tab) => tab.id === activeSubTab)}
          onChange={handleSubTabChange}
        >
          {RouteTabConfig.map((tab) => {
            const IconComponent =
              activeSubTab === tab.id ? tab.selectedIcon : tab.unselectedIcon;
            const Icon = IconComponent || Icons.UserFilled;
            return (
              <Tab
                key={tab.id}
                icon={
                  <Icon
                    style={{
                      height: '18px',
                      width: '18px',
                      marginRight: 8
                    }}
                  />
                }
                sx={{
                  fontWeight: '500',
                  color: pallet.formLabelText,
                  textTransform: 'capitalize',
                  fontSize: '15px'
                }}
                iconPosition='start'
                label={tab.label}
              />
            );
          })}
        </Tabs>
        <Button
          variant='contained'
          onClick={() => setIsAddRouteDialogOpen(true)}
        >
          <Icons.PlusWhite style={{ marginRight: '6px' }} />
          Add A New Route
        </Button>
      </Box>
      <Divider sx={{ mb: 3, maxWidth: '420px' }} />
      <RoutesTable
        routes={filteredRoutes}
        loading={loading}
        onEdit={handleEditRoute}
        onDelete={handleDeleteRoute}
        onSOSAlert={handleSOSAlert}
      />

      <AddRouteDialog
        open={isAddRouteDialogOpen}
        onClose={() => setIsAddRouteDialogOpen(false)}
        onAddRoute={handleAddRoute}
        loggedInUser={loggedInUser}
        showToast={showToast}
        buses={buses}
      />
      <EditRouteDialog
        open={isEditRouteDialogOpen}
        onClose={() => setIsEditRouteDialogOpen(false)}
        onEditRoute={handleEditRouteSuccess}
        loggedInUser={loggedInUser}
        showToast={showToast}
        route={routeToEdit}
        buses={buses}
      />

      <DialogModal
        open={isSOSConfirmDialogOpen}
        onClose={() => setIsSOSConfirmDialogOpen(false)}
        title='Send SOS Alert'
        actions={
          <>
            <Button
              onClick={() => setIsSOSConfirmDialogOpen(false)}
              variant='contained'
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmSOS} variant='contained' autoFocus>
              Send Alert
            </Button>
          </>
        }
      >
        Are you sure you want to send an alert to notify all parents of the
        route {routeForSOS?.name} that bus-tracking alerts would be stopped?
      </DialogModal>

      <DialogModal
        open={isDeleteConfirmDialogOpen}
        onClose={() => setIsDeleteConfirmDialogOpen(false)}
        title='Delete Route'
        actions={
          <>
            <Button
              onClick={() => setIsDeleteConfirmDialogOpen(false)}
              variant='contained'
              color='primary'
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color='error'
              variant='contained'
              autoFocus
              sx={{
                backgroundColor: pallet.unlinkRed,
                color: pallet.white,
                '&:hover': {
                  backgroundColor: pallet.unlinkRed
                }
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to{' '}
        <Typography
          component='span'
          sx={{ color: pallet.unlinkRed, fontWeight: 'bold' }}
        >
          delete{' '}
        </Typography>
        the route{' '}
        <Typography
          component='span'
          sx={{ color: pallet.unlinkRed, fontWeight: 'bold' }}
        >
          {routeToDelete?.name}
        </Typography>
        ?
      </DialogModal>
    </>
  );
};

export default RoutesContainer;
